import { $, merge } from "glamor";
import * as React from "react";
import { IInfiniteBaseProps } from "./infinite-interface";
import { Item as ItemCard } from "./item";
import { result } from "./infinite-utils/infinite-utils-result";
import { amphibiousGetIn, amphibiousGet } from "helpers/objUtils";
import { Item } from "constants/product-collection";
import { DATATEST } from "constants/integration";
import { List } from "immutable";

export class InfiniteBase extends React.PureComponent<IInfiniteBaseProps, any> {
    childrenToDisplay: React.ReactNode[];
    scrollHeight: number;
    scrollContainerRef = React.createRef<HTMLDivElement>();

    constructor(props) {
        super(props);

        this.state = {
            halfHeightGroupHeader: 0,
        };
    }

    styles = merge(
        $(".innerScrollContainer", {
            width: "auto",
            overflow: "visible",
        }),
        $(".innerScrollContainer > *", {
            transition: "all .25s ease-in-out",
            contain: "layout",
        }),
    );

    UNSAFE_componentWillMount() {
        this.calculateChildrenToRender();
    }

    UNSAFE_componentWillUpdate(nextProps: IInfiniteBaseProps) {
        this.calculateChildrenToRender(nextProps);
    }

    render() {
        return (
            <div
                {...this.styles}
                ref={this.scrollContainerRef}
                className="innerScrollContainer"
                role="rowgroup"
                style={{
                    height: this.scrollHeight,
                    ...(this.props.isScrolling ? { pointerEvents: "none" } : {}),
                }}
            >
                {this.childrenToDisplay}
            </div>
        );
    }

    private addChildToRender = (node: React.ReactNode) => {
        this.childrenToDisplay.push(node);
    };

    private calculateChildrenToRender(props = this.props) {
        const {
            items,
            groupedItems,
            groupKey,
            containerWidth,
            containerHeight,
            isGrouped,
            onGetCellDisplayInfo,
            centered,
            gutterWidth,
            maxWidth,
        } = props;

        this.childrenToDisplay = [];

        if (containerHeight === 0 || containerWidth === 0 || !items) {
            return;
        }

        const data = amphibiousGet(items, 0);
        const dimensions = result(onGetCellDisplayInfo, { index: 0, data });
        const mediaQueryViewport = window.matchMedia("(max-width: 768px)");

        let startingXPosition = gutterWidth;
        if (centered) {
            const containerMaxWidth = Math.min(containerWidth, maxWidth);
            const offset = (containerWidth - containerMaxWidth) / 2;
            startingXPosition =
                offset +
                gutterWidth +
                (containerMaxWidth -
                    gutterWidth * 2 -
                    Math.floor((containerMaxWidth - gutterWidth * 2) / dimensions.width) * dimensions.width) /
                    2;
        }

        const cursor = {
            top: 0,
            left: startingXPosition,
        };

        let halfHeightGroupHeader = 0;

        if (this.scrollContainerRef?.current?.childNodes) {
            const groupHeaderParent = this.scrollContainerRef.current.childNodes[0] as HTMLElement;
            const groupHeader = groupHeaderParent?.getElementsByClassName("groupHeader");
            halfHeightGroupHeader = groupHeader?.length ? groupHeader[0].clientHeight / 2 : 0;

            if (halfHeightGroupHeader !== this.state.halfHeightGroupHeader) {
                this.setState({ halfHeightGroupHeader });
            }
        }

        // Assume everything is grouped
        let groupIndex = 0;
        if (isGrouped) {
            groupedItems.forEach((groupItem) => {
                const params = {
                    cursor,
                    items: groupItem.get("items"),
                    groupKeyValues: groupItem.get("groupKeyValues"),
                    groupKey,
                    addChildToRender: this.addChildToRender,
                    groupIndex,
                    startingXPosition,
                };
                // TODO: render header
                props.api.trigger("beforeGroupRender", params);
                if (mediaQueryViewport.matches) {
                    cursor.top = cursor.top + halfHeightGroupHeader;
                }
                this.renderGroupItems(props, params);
                props.api.trigger("afterGroupRender", params);
                groupIndex = groupIndex + 1;
            });
        } else {
            const params = {
                cursor,
                items,
                addChildToRender: this.addChildToRender,
                groupIndex,
                startingXPosition,
            };
            // if uniform size
            // if (isUniformedCells) {
            //     this.quickRenderUniformItems(props, params);
            // } else {
            //     this.renderGroupItems(props, params);
            // }
            this.renderGroupItems(props, params);
        }

        props.api.trigger("afterRenderCells", {
            cursor,
            childrenToDisplay: this.childrenToDisplay,
            ...this.props,
        });

        this.scrollHeight = cursor.top;
    }

    private quickRenderUniformItems(props, params) {
        const { cursor, items, groupIndex } = params;
        const { containerWidth, containerHeight, onGetCellDisplayInfo, scrollTop } = props;
        const bottom = scrollTop + containerHeight;

        const dimensions = onGetCellDisplayInfo;
        const columns = dimensions.width
            ? Math.floor(containerWidth / dimensions.width) || 1 // cant be 0
            : 1;
        const rows = Math.floor(items.size / columns);

        // calculate what cell to start
        const rowsFromTheTop = Math.floor(scrollTop / dimensions.height);
        const startIndex = rowsFromTheTop * columns;

        // forward top to start of rendering
        cursor.top = rowsFromTheTop * dimensions.height;

        // iterate through all displayed cells, then break
        for (let i = startIndex; i < items.size; i++) {
            const data = amphibiousGet(items, i);

            if (dimensions.width) {
                if (cursor.left + dimensions.width > containerWidth) {
                    cursor.left = 0;
                    cursor.top = cursor.top + dimensions.height;
                }
            }
            if (cursor.top > bottom) {
                break;
            }
            if (cursor.top + dimensions.height >= scrollTop) {
                this.childrenToDisplay.push(
                    this.renderCell(props, {
                        position: {
                            top: cursor.top,
                            left: cursor.left,
                        },
                        data,
                        dataIndex: i,
                        groupIndex,
                    }),
                );
            }

            if (dimensions.width) {
                cursor.left = cursor.left + dimensions.width;
                if (i === items.size - 1) {
                    cursor.left = 0;
                    cursor.top = cursor.top + dimensions.height;
                }
            } else {
                cursor.top = cursor.top + dimensions.height;
            }
        }

        // calculate total height for scroll container
        cursor.top = rows * dimensions.height;
    }

    private renderGroupItems(props, params) {
        const { cursor, items, groupIndex, startingXPosition, groupKeyValues } = params;
        const {
            containerWidth,
            containerHeight,
            onGetCellDisplayInfo,
            scrollTop,
            draggingId,
            snapshotItems,
            infinite,
            maxWidth,
            gutterWidth,
        } = props;
        const constrainedOffer = (containerWidth - Math.min(containerWidth, maxWidth)) / 2;
        const containerWidthWithoutGutter = containerWidth - gutterWidth;
        const widthConstraint = containerWidthWithoutGutter - constrainedOffer;
        const bottom = scrollTop + containerHeight;
        const { halfHeightGroupHeader } = this.state;
        /* snapshotItems - the actual original array of items which is required for getting relative indexes
           origIndex - used to find the relative index of the item with respect to the original array
           lastOrigIndex - This is used to fetch the relative index of the last item in each group.
        */
        let lastOrigIndex = items.size - 1;
        const lastItemInGroup = amphibiousGet(items, lastOrigIndex);

        /* This if condition is required to pre-calculate the lastOrigIndex before proceeding with the main loop */
        if (snapshotItems) {
            lastOrigIndex = snapshotItems.findIndex(
                (snapshotItem) =>
                    amphibiousGetIn(lastItemInGroup, Item.FIELDS.ID) === amphibiousGetIn(snapshotItem, Item.FIELDS.ID),
            );
        }

        for (let i = 0; i < items.size; i++) {
            const data = amphibiousGet(items, i);
            const dimensions = result(onGetCellDisplayInfo, { index: i, data });
            let origIndex = i;
            if (snapshotItems) {
                origIndex = snapshotItems.findIndex(
                    (snapshotItem) =>
                        amphibiousGetIn(data, Item.FIELDS.ID) === amphibiousGetIn(snapshotItem, Item.FIELDS.ID),
                );
            }

            const eventData = {
                index: origIndex,
                lastOrigIndex,
                dimensions,
                containerWidth,
                isLastIteminGroup: i === items.size - 1,
                ...params,
            };

            if (draggingId && draggingId === amphibiousGetIn(data, Item.FIELDS.ID)) {
                this.childrenToDisplay.push(
                    this.renderCell(props, {
                        position: {
                            top: cursor.top + halfHeightGroupHeader,
                            left: cursor.left,
                        },
                        data,
                        dataIndex: i,
                        display: "none",
                    }),
                );
                /* As soon as last item in the group is clicked for dragging, the items in next group
                   push to cover the existing gap. To avoid filling the gap, we need to set the cursor
                   position accordingly */
                if (i === items.size - 1 && dimensions.height) {
                    cursor.left = startingXPosition;
                    cursor.top = cursor.top + dimensions.height + halfHeightGroupHeader;
                }
                continue;
            }

            if (dimensions.width) {
                if (cursor.left + dimensions.width > widthConstraint) {
                    cursor.left = startingXPosition;
                    cursor.top = cursor.top + dimensions.height + halfHeightGroupHeader;
                }
            }

            props.api.trigger("beforeRenderCell", eventData);

            if (dimensions.width) {
                if (cursor.left + dimensions.width > widthConstraint) {
                    cursor.left = startingXPosition;
                    cursor.top = cursor.top + dimensions.height + halfHeightGroupHeader;
                }
            }

            if (infinite === false || (cursor.top + dimensions.height >= scrollTop && cursor.top <= bottom)) {
                this.childrenToDisplay.push(
                    this.renderCell(props, {
                        position: {
                            top: cursor.top + halfHeightGroupHeader,
                            left: cursor.left,
                        },
                        data,
                        dataIndex: i,
                        groupIndex,
                        groupKeyValues,
                    }),
                );
            }

            if (dimensions.width) {
                cursor.left = cursor.left + dimensions.width;
                if (i === items.size - 1) {
                    cursor.left = startingXPosition;
                    cursor.top = cursor.top + dimensions.height + halfHeightGroupHeader;
                }
            } else {
                cursor.top = cursor.top + dimensions.height + halfHeightGroupHeader;
            }

            props.api.trigger("afterRenderCell", eventData);
        }
    }

    private renderCell(
        props,
        {
            position: { top, left },
            dataIndex = -1,
            data,
            zIndex = 1,
            groupIndex = 0,
            display = "block",
            groupKeyValues = List<string>(),
        },
    ) {
        const style: React.CSSProperties = {
            position: "absolute",
            WebkitTransform: `translate3d(${left}px, ${top}px, 0px)`,
            transform: `translate3d(${left}px, ${top}px, 0px)`,
            zIndex,
            display,
        };

        const id = typeof data === "object" ? amphibiousGetIn(data, props.itemIdPath) : data;
        const children = props.cellRenderer({
            dataIndex,
            data,
            isSortable: props.isSortable,
            isGrouped: props.isGrouped,
            groupKeyValues,
        });

        return (
            <ItemCard
                data-test={DATATEST.TILE_CARD}
                key={groupIndex + "_" + dataIndex}
                style={style}
                data-id={id}
                data-group-index={groupIndex}
                data-sortable={true}
                className="infiniteCell"
            >
                {children}
            </ItemCard>
        );
    }
}
